import React, { useState } from "react"
import Icon from "../../../components/AppIcon"
import Button from "../../../components/ui/Button"
import Select from "../../../components/ui/Select"

const CHART_COLOR_VARS = ["--chart-1", "--chart-2", "--chart-3", "--chart-4", "--chart-5"]
const colorFor = (i) => `var(${CHART_COLOR_VARS[i % CHART_COLOR_VARS.length]})`

const LearningPathBuilder = ({ path, bundledCourseIds, courses, onChange }) => {
  const [dragIndex, setDragIndex] = useState(null)
  const [addCourseId, setAddCourseId] = useState("")

  const courseTitle = (courseId) => courses?.find((c) => c?.courseId === courseId)?.title || "Untitled Course"
  const pathedCourseIds = new Set(path?.map((n) => n?.courseId))
  const availableToAdd = bundledCourseIds?.filter((id) => !pathedCourseIds.has(id)) || []

  const addNode = () => {
    if (!addCourseId) return
    onChange([...(path || []), { id: `node-${Date.now()}`, courseId: addCourseId, required: true, milestone: false }])
    setAddCourseId("")
  }

  const removeNode = (id) => onChange(path?.filter((n) => n?.id !== id))
  const updateNode = (id, updates) => onChange(path?.map((n) => (n?.id === id ? { ...n, ...updates } : n)))

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) return
    const next = [...path]
    const [moved] = next.splice(dragIndex, 1)
    next.splice(dropIndex, 0, moved)
    onChange(next)
    setDragIndex(null)
  }

  if (!bundledCourseIds || bundledCourseIds?.length === 0) {
    return (
      <div className="bg-card border border-dashed border-border rounded-xl p-12 text-center">
        <Icon name="Route" size={28} className="text-muted-foreground mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Add courses to this program first, then sequence them into a path here.</p>
      </div>
    )
  }

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-1">Learning Path</h3>
      <p className="text-sm text-muted-foreground mb-6">Drag to reorder. Learners move through these courses top to bottom.</p>

      {path?.length === 0 ? (
        <div className="border border-dashed border-border rounded-lg p-8 text-center mb-4">
          <p className="text-sm text-muted-foreground">No sequence yet — add a course below to start the path.</p>
        </div>
      ) : (
        <div className="relative">
          <div
            className="absolute left-[27px] top-6 bottom-6 w-px opacity-40"
            style={{ background: `linear-gradient(to bottom, ${colorFor(0)}, ${colorFor(2)}, ${colorFor(4)})` }}
          />
          <div className="space-y-3">
            {path?.map((node, i) => (
              <div
                key={node?.id}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(i)}
                className={`relative flex items-start gap-4 bg-background border border-border rounded-lg p-4 transition-shadow cursor-grab active:cursor-grabbing hover:shadow-md ${dragIndex === i ? "opacity-50" : ""}`}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 mt-0.5"
                  style={{ backgroundColor: colorFor(i) }}
                >
                  {i + 1}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground truncate">{courseTitle(node?.courseId)}</p>
                    {node?.milestone && (
                      <span className="flex items-center gap-1 text-xs text-warning bg-warning/10 px-2 py-0.5 rounded-full">
                        <Icon name="Star" size={11} /> Milestone
                      </span>
                    )}
                    {node?.required === false && (
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Optional</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-2">
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={node?.required !== false}
                        onChange={(e) => updateNode(node?.id, { required: e?.target?.checked })}
                        className="rounded border-border"
                      />
                      Required
                    </label>
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!node?.milestone}
                        onChange={(e) => updateNode(node?.id, { milestone: e?.target?.checked })}
                        className="rounded border-border"
                      />
                      Mark as milestone
                    </label>
                  </div>
                </div>

                <Icon name="GripVertical" size={16} className="text-muted-foreground shrink-0 mt-1" />
                <Button variant="ghost" size="icon" iconName="X" iconSize={14} onClick={() => removeNode(node?.id)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {availableToAdd?.length > 0 && (
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
          <div className="flex-1">
            <Select
              options={[{ value: "", label: "Add a course to the path..." }, ...availableToAdd.map((id) => ({ value: id, label: courseTitle(id) }))]}
              value={addCourseId}
              onChange={setAddCourseId}
            />
          </div>
          <Button variant="outline" iconName="Plus" iconPosition="left" onClick={addNode} disabled={!addCourseId}>
            Add
          </Button>
        </div>
      )}
    </div>
  )
}

export default LearningPathBuilder